import { createTRPCClient, httpLink } from '@trpc/client'
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server'
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query'
import { useAuthStore } from 'shared/store/auth'
import { authenticatedFetch } from 'shared/lib/auth/recovery'
import superjson from 'superjson'
import type { AdminRouter, MobileRouter } from './app-router'
import { getApiBaseUrl } from './base-url'
import { queryClient } from './tanstack-query-config'

export type MobileInputs = inferRouterInputs<MobileRouter>
export type MobileOutputs = inferRouterOutputs<MobileRouter>
export type AdminInputs = inferRouterInputs<AdminRouter>
export type AdminOutputs = inferRouterOutputs<AdminRouter>

function getDevToken(): string | null {
	if (typeof window === 'undefined') return null
	const cookieMatch = document.cookie.split(';').find((c) => {
		const [name] = c.trim().split('=')
		return name === 'dev_mode'
	})
	if (cookieMatch) {
		const [, ...rest] = cookieMatch.trim().split('=')
		return rest.join('=').trim()
	}
	return null
}

/** Bearer-JWT header from the persisted auth store and x-dev-mode header if unlocked. */
function authHeaders(): Record<string, string> {
	const token = useAuthStore.getState().token
	const devToken = getDevToken()
	const headers: Record<string, string> = {}
	if (token) headers.authorization = `Bearer ${token}`
	if (devToken) headers['x-dev-mode'] = devToken
	return headers
}

const base = getApiBaseUrl()

export const mobileTrpcClient = createTRPCClient<MobileRouter>({
	links: [
		httpLink({
			transformer: superjson,
			url: `${base}/trpc/mobile`,
			headers: authHeaders,
			fetch: authenticatedFetch,
		}),
	],
})

export const adminTrpcClient = createTRPCClient<AdminRouter>({
	links: [
		httpLink({
			transformer: superjson,
			url: `${base}/trpc/admin`,
			headers: authHeaders,
			fetch: authenticatedFetch,
		}),
	],
})

export const mobileTrpc = createTRPCOptionsProxy<
	MobileRouter,
	{ keyPrefix: true }
>({
	client: mobileTrpcClient,
	queryClient,
	keyPrefix: 'mobile',
})

export const adminTrpc = createTRPCOptionsProxy<
	AdminRouter,
	{ keyPrefix: true }
>({
	client: adminTrpcClient,
	queryClient,
	keyPrefix: 'admin',
})

/**
 * Buyer (TMA) tRPC hooks — `/trpc/mobile`.
 * @example const trpc = useMobileTrpc(); useQuery(trpc.lots.Feed.queryOptions())
 */
export function useMobileTrpc() {
	return mobileTrpc
}

/**
 * Admin tRPC hooks — `/trpc/admin`.
 * @example const trpc = useAdminTrpc(); useQuery(trpc.purchases.ListHistory.queryOptions())
 */
export function useAdminTrpc() {
	return adminTrpc
}
